import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, LogOut, Search, MapPin } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { getAuthHeaders, getUser, logout } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  offered: 'bg-blue-100 text-blue-800 border-blue-200',
  accepted: 'bg-green-100 text-green-800 border-green-200',
  in_transit: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-slate-100 text-slate-800 border-slate-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackInput, setTrackInput] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'customer') {
      navigate('/customer/login');
      return;
    }
    axios
      .get(`${BACKEND_URL}/api/dispatch/jobs`, { headers: getAuthHeaders() })
      .then((res) => setJobs(res.data.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const openTracking = (number) => {
    if (number) navigate(`/tracking?number=${encodeURIComponent(number)}`);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-dashboard">
      <header className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="h-7 w-7" />
            <div>
              <div className="font-bold leading-tight">Sabey Logistics</div>
              <div className="text-xs text-slate-300">Customer Portal</div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-200" data-testid="customer-name">{user?.full_name}</span>
            <button onClick={handleLogout} className="p-2 hover:bg-slate-700 rounded-lg" data-testid="customer-logout-btn" aria-label="Log out">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <Card className="p-6 border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Track a Shipment</h2>
          <div className="flex gap-3">
            <Input
              value={trackInput}
              onChange={(e) => setTrackInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && openTracking(trackInput.trim())}
              placeholder="Enter tracking or job number"
              className="border-slate-300"
              data-testid="customer-track-input"
            />
            <Button onClick={() => openTracking(trackInput.trim())} className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="customer-track-btn">
              <Search className="h-4 w-4 mr-2" /> Track
            </Button>
          </div>
        </Card>

        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">My Shipments</h2>
          {loading ? (
            <p className="text-slate-500" data-testid="customer-jobs-loading">Loading shipments...</p>
          ) : jobs.length === 0 ? (
            <Card className="p-10 text-center border-slate-200" data-testid="customer-jobs-empty">
              <p className="text-slate-600">No shipments are linked to your account yet.</p>
              <p className="text-sm text-slate-500 mt-1">Use the tracking box above to look up any shipment by number.</p>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="customer-jobs-list">
              {jobs.map((job) => (
                <Card key={job.id} className="p-5 border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`customer-job-${job.job_number}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-slate-900">{job.job_number}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[job.status] || 'bg-gray-100 text-gray-800'}`}>
                        {job.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-slate-600 mt-2">
                      <MapPin className="h-4 w-4 mr-1 text-slate-400" />
                      {job.pickup_location?.city}, {job.pickup_location?.state} &rarr; {job.delivery_location?.city}, {job.delivery_location?.state}
                    </div>
                    {job.tracking_number && <div className="text-xs text-slate-500 mt-1">Tracking: {job.tracking_number}</div>}
                  </div>
                  <Button variant="outline" onClick={() => openTracking(job.tracking_number || job.job_number)} className="border-slate-300" data-testid={`customer-job-track-${job.job_number}`}>
                    View Tracking
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CustomerDashboard;
